'use client';

import { useEffect } from 'react';

import { DEFAULT_DUPLICATE_ACTION_WINDOW_MS, DuplicateActionTracker } from './duplicateActionTracker';

export { DEFAULT_DUPLICATE_ACTION_WINDOW_MS, DuplicateActionTracker } from './duplicateActionTracker';

const CLICK_ACTION_SELECTOR = [
	'button',
	'input[type="button"]',
	'input[type="image"]',
	'input[type="submit"]',
	'input[type="reset"]',
	'[role="button"]',
].join(',');

const ALLOW_REPEAT_CLICK_SELECTOR = '[data-allow-repeat-click="true"]';
const ALLOW_REPEAT_SUBMIT_SELECTOR = '[data-allow-repeat-submit="true"]';

const suppressDuplicateAction = (event: Event) => {
	event.preventDefault();
	event.stopPropagation();
	event.stopImmediatePropagation();
};

const getClosestElement = (event: Event, selector: string): Element | null => {
	if (!(event.target instanceof Element)) {
		return null;
	}

	return event.target.closest(selector);
};

export type DuplicateActionGuardProps = {
	/** The minimum time between accepted actions on the same control. */
	windowMs?: number;
};

export const isProgrammaticClick = (event: Pick<MouseEvent, 'isTrusted'>): boolean => !event.isTrusted;

export const containsFileInput = (action: Pick<Element, 'querySelector'>): boolean =>
	Boolean(action.querySelector('input[type="file"]'));

/**
 * Prevents a rapid second click or form submission from invoking an action
 * twice while React is still applying the action's loading/disabled state.
 *
 * Add `data-allow-repeat-click="true"` to a control or ancestor when rapid
 * repeated clicks are intentional. Forms can opt out with
 * `data-allow-repeat-submit="true"`.
 */
export const DuplicateActionGuard = ({ windowMs = DEFAULT_DUPLICATE_ACTION_WINDOW_MS }: DuplicateActionGuardProps) => {
	useEffect(() => {
		const clickTracker = new DuplicateActionTracker(windowMs);
		const submitTracker = new DuplicateActionTracker(windowMs);

		const handleClick = (event: MouseEvent) => {
			// Controls such as file-upload buttons intentionally call input.click().
			// That synthetic click is untrusted and must reach the hidden input.
			if (isProgrammaticClick(event)) {
				return;
			}

			const action = getClosestElement(event, CLICK_ACTION_SELECTOR);
			if (!action || action.closest(ALLOW_REPEAT_CLICK_SELECTOR) || containsFileInput(action)) {
				return;
			}

			if (clickTracker.isDuplicate(action)) {
				suppressDuplicateAction(event);
			}
		};

		const handleSubmit = (event: SubmitEvent) => {
			const form = getClosestElement(event, 'form');
			if (!form || form.closest(ALLOW_REPEAT_SUBMIT_SELECTOR)) {
				return;
			}

			if (submitTracker.isDuplicate(form)) {
				suppressDuplicateAction(event);
			}
		};

		document.addEventListener('click', handleClick, true);
		document.addEventListener('submit', handleSubmit, true);

		return () => {
			document.removeEventListener('click', handleClick, true);
			document.removeEventListener('submit', handleSubmit, true);
		};
	}, [windowMs]);

	return null;
};
